import React, { useState } from 'react';
import {
  AlertStyle,
  cardBackground,
  innerItemsClass,
  positionClasses,
  slideFrom,
  textAlignClass,
  textSizeClasses,
} from '../../utils/alert-style';

import MemberBadge from '../membership/member-badge';
import AlertMedal from './alert-medal';
import { MedalTone } from '../../utils/alert-kind';

// Vẽ một thông báo donate theo giao diện streamer chọn. Dùng chung cho overlay OBS
// (page-donate-livestream) và khung xem trước trong Cài đặt Donate.

type Frame = { width: number; height: number };

export interface DonateAlertViewProps {
  gif: string | null;
  text: string;
  message?: string;
  /** Người gửi là hội viên kênh: hiện huy hiệu đầu dòng chính. */
  isMember?: boolean;
  color: string;
  style: AlertStyle;
  /** Đổi giá trị này để chạy lại hiệu ứng xuất hiện (và nạp lại ảnh động). */
  animKey?: string | number;
  /**
   * Khung ảo cho xem trước: đơn vị vh/vw được quy ra px theo khung này thay vì theo cửa sổ.
   * Bỏ trống trên overlay (toàn màn hình OBS).
   */
  frame?: Frame;
  /** Phần tử không hiển thị đặt bên trong container (overlay đặt thẻ <audio> ở đây như trước). */
  children?: React.ReactNode;
  /** Huy hiệu đầu dòng chính (kiểu Nổi bật bạc, Đặc biệt vàng). */
  medal?: { tone: MedalTone; url: string | null } | null;
  /** Viền phát sáng quanh thông báo (kiểu Nổi bật). */
  glow?: boolean;
}

// Keyframes khai báo một lần; tên đều có tiền tố cab-alert- để không đụng CSS khác.
const CSS = `
@keyframes cab-alert-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes cab-alert-slide-in-left { from { opacity: 0; transform: translateX(-120%); } to { opacity: 1; transform: none; } }
@keyframes cab-alert-slide-in-right { from { opacity: 0; transform: translateX(120%); } to { opacity: 1; transform: none; } }
@keyframes cab-alert-slide-in-top { from { opacity: 0; transform: translateY(-120%); } to { opacity: 1; transform: none; } }
@keyframes cab-alert-slide-in-bottom { from { opacity: 0; transform: translateY(120%); } to { opacity: 1; transform: none; } }
@keyframes cab-alert-zoom-in { from { opacity: 0; transform: scale(0.3); } 70% { opacity: 1; transform: scale(1.05); } to { opacity: 1; transform: none; } }
@keyframes cab-alert-bounce-in {
  0% { opacity: 0; transform: translateY(-60px); }
  45% { opacity: 1; transform: translateY(0); }
  62% { transform: translateY(-22px); }
  78% { transform: translateY(0); }
  90% { transform: translateY(-7px); }
  100% { opacity: 1; transform: none; }
}
.cab-alert-anim-fade { animation: cab-alert-fade-in 0.45s ease-out both; }
.cab-alert-anim-slide-left { animation: cab-alert-slide-in-left 0.55s cubic-bezier(0.22, 1, 0.36, 1) both; }
.cab-alert-anim-slide-right { animation: cab-alert-slide-in-right 0.55s cubic-bezier(0.22, 1, 0.36, 1) both; }
.cab-alert-anim-slide-top { animation: cab-alert-slide-in-top 0.55s cubic-bezier(0.22, 1, 0.36, 1) both; }
.cab-alert-anim-slide-bottom { animation: cab-alert-slide-in-bottom 0.55s cubic-bezier(0.22, 1, 0.36, 1) both; }
.cab-alert-anim-zoom { animation: cab-alert-zoom-in 0.5s ease-out both; }
.cab-alert-anim-bounce { animation: cab-alert-bounce-in 0.9s ease-out both; }
@keyframes cab-alert-kind-glow-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
.cab-alert-kind-glow { position: relative; }
.cab-alert-kind-glow::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 20px;
  border: 3px solid #CFE8FF;
  box-shadow: 0 0 18px #8FD3FF, 0 0 36px rgba(143, 211, 255, 0.6), inset 0 0 14px #8FD3FF;
  pointer-events: none;
  animation: cab-alert-kind-glow-pulse 1.4s ease-in-out infinite;
}
.cab-alert-kind-glow.overflow-hidden::after { inset: 0; border-radius: inherit; }
`;

const animClass = (s: AlertStyle) =>
  s.animation === 'slide' ? `cab-alert-anim-slide-${slideFrom(s.position)}` : `cab-alert-anim-${s.animation}`;

const SOFT_SHADOW = '0 2px 4px rgba(0, 0, 0, 0.6)';
const STRONG_SHADOW = '0 2px 6px rgba(0, 0, 0, 0.9), 0 0 2px rgba(0, 0, 0, 0.9)';

type BodyProps = Omit<DonateAlertViewProps, 'animKey' | 'children'>;

// Tách riêng + gắn key từ bên ngoài để mỗi thông báo mới có state ảnh lỗi mới và hiệu ứng chạy lại.
const AlertBody: React.FC<BodyProps> = ({ gif, text, message, isMember, color, style, frame, medal, glow }) => {
  const [imgFailed, setImgFailed] = useState(false);
  // vh/vw theo khung ảo khi xem trước; trên overlay giữ lớp Tailwind (undefined = không ghi đè).
  const vh = (n: number) => (frame ? `${(frame.height * n) / 100}px` : undefined);
  const vw = (n: number) => (frame ? `${(frame.width * n) / 100}px` : undefined);

  const size = textSizeClasses(style.textSize);
  const align = textAlignClass(style.position);
  const items = innerItemsClass(style.position);
  const bg = cardBackground(style.cardColor, style.cardOpacity);
  const anim = `${animClass(style)}${glow ? ' cab-alert-kind-glow' : ''}`;
  const showImg = !!gif && !imgFailed;
  const onImgError = () => setImgFailed(true);

  const mainText = (extra: string, shadow?: string) => (
    <div className={`${size.main} font-extrabold break-words ${extra}`} style={{ color, textShadow: shadow }}>
      {medal && <AlertMedal tone={medal.tone} url={medal.url} className="mr-2 align-text-bottom" />}
      {isMember && <MemberBadge size={28} className="inline-block mr-2 align-text-bottom" />}
      {text}
    </div>
  );
  const messageText = (extra: string, shadow?: string) =>
    message ? (
      <div className={`${size.message} font-extrabold text-white break-words ${extra}`} style={{ textShadow: shadow }}>
        {message}
      </div>
    ) : null;

  switch (style.layout) {
    case 'card':
      return (
        <div
          className={`${anim} flex flex-row items-center gap-4 rounded-2xl px-6 py-4 max-w-[90vw]`}
          style={{ background: bg, maxWidth: vw(90) }}
        >
          {showImg && (
            <img
              alt="gif"
              src={gif as string}
              className="max-h-[25vh] max-w-[25vw] object-contain shrink-0"
              style={{ maxHeight: vh(25), maxWidth: vw(25) }}
              onError={onImgError}
            />
          )}
          <div className="flex flex-col gap-1 min-w-0 text-left">
            {mainText('', SOFT_SHADOW)}
            {messageText('', SOFT_SHADOW)}
          </div>
        </div>
      );

    case 'banner': {
      const justify = { 'text-left': 'justify-start', 'text-center': 'justify-center', 'text-right': 'justify-end' }[
        align
      ];
      return (
        <div className={`${anim} w-full flex flex-row items-center ${justify} gap-6 px-8 py-4`} style={{ background: bg }}>
          {showImg && (
            <img
              alt="gif"
              src={gif as string}
              className="max-h-[18vh] max-w-[25vw] object-contain shrink-0"
              style={{ maxHeight: vh(18), maxWidth: vw(25) }}
              onError={onImgError}
            />
          )}
          <div className={`flex flex-col gap-1 min-w-0 ${align}`}>
            {mainText('', SOFT_SHADOW)}
            {messageText('', SOFT_SHADOW)}
          </div>
        </div>
      );
    }

    case 'minimal':
      return (
        <div className={`${anim} flex flex-col gap-1 max-w-[90vw] ${items} ${align}`} style={{ maxWidth: vw(90) }}>
          {mainText('', STRONG_SHADOW)}
          {messageText('', STRONG_SHADOW)}
        </div>
      );

    case 'glow':
      return (
        <div
          className={`${anim} flex flex-col items-center gap-3 rounded-2xl border-2 px-8 py-6 max-w-[90vw] text-center`}
          style={{
            background: bg,
            borderColor: color,
            boxShadow: `0 0 24px ${color}, inset 0 0 14px ${color}`,
            maxWidth: vw(90),
          }}
        >
          {showImg && (
            <img
              alt="gif"
              src={gif as string}
              className="max-h-[40vh] object-contain"
              style={{ maxHeight: vh(40) }}
              onError={onImgError}
            />
          )}
          {mainText('', SOFT_SHADOW)}
          {messageText('', SOFT_SHADOW)}
        </div>
      );

    case 'poster':
      if (!showImg) {
        // Không có ảnh thì không còn gì để chữ đè lên: hiện như một thẻ chữ.
        return (
          <div
            className={`${anim} flex flex-col gap-1 rounded-2xl px-8 py-6 max-w-[90vw] ${align}`}
            style={{ background: bg, maxWidth: vw(90) }}
          >
            {mainText('', SOFT_SHADOW)}
            {messageText('', SOFT_SHADOW)}
          </div>
        );
      }
      return (
        <div className={`${anim} relative overflow-hidden rounded-2xl max-w-[90vw]`} style={{ maxWidth: vw(90) }}>
          <img
            alt="gif"
            src={gif as string}
            className="block h-[60vh] w-auto max-w-[90vw] object-contain"
            style={{ height: vh(60), maxWidth: vw(90) }}
            onError={onImgError}
          />
          <div
            className={`absolute inset-x-0 bottom-0 min-h-[50%] flex flex-col justify-end gap-1 px-6 pb-5 pt-10 ${align}`}
            style={{ background: `linear-gradient(to top, ${bg} 55%, transparent)` }}
          >
            {mainText('', SOFT_SHADOW)}
            {messageText('', SOFT_SHADOW)}
          </div>
        </div>
      );

    default:
      // Cổ điển: tương đương markup overlay trước đây (ảnh trên, dòng chính màu, lời nhắn trắng).
      return (
        <div className={`${anim} flex flex-col gap-2 max-w-[90vw] ${items}`} style={{ maxWidth: vw(90) }}>
          {showImg && (
            <img
              alt="gif"
              src={gif as string}
              className="max-h-[60vh] object-contain"
              style={{ maxHeight: vh(60) }}
              onError={onImgError}
            />
          )}
          {mainText(`${align} px-4`)}
          {messageText(`text-stroke ${align} px-4`)}
        </div>
      );
  }
};

const containerClasses = (s: AlertStyle) => {
  const pos = positionClasses(s.position);
  if (s.layout === 'classic') return `flex ${pos} flex-col gap-2 py-4 h-screen`; // = overlay cũ khi ở giữa
  if (s.layout === 'banner') return `h-screen w-full flex flex-col ${pos} py-6`; // dải ngang sát hai mép
  return `h-screen w-full flex flex-col p-6 ${pos}`;
};

const DonateAlertView: React.FC<DonateAlertViewProps> = ({ animKey, children, ...body }) => (
  <div
    className={containerClasses(body.style)}
    style={body.frame ? { width: body.frame.width, height: body.frame.height } : undefined}
  >
    <style>{CSS}</style>
    <AlertBody key={`${animKey ?? ''}|${body.gif ?? ''}`} {...body} />
    {children}
  </div>
);

export default DonateAlertView;
